import { memo } from "react";
import { motion } from "framer-motion";
import {
  Trophy,
  LayoutDashboard,
  TrendingUp,
  Award,
  Sparkles,
  ClipboardCheck,
  Smartphone,
  Plus,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { useTranslation } from "react-i18next";
import { FeatureDetailDialog } from "@/components/landing/FeatureDetailDialog";
import { FeatureMiniMockup, type MiniMockupVariant } from "@/components/landing/FeatureMiniMockup";
import { useFeatureHoverPopup } from "@/components/landing/useFeatureHoverPopup";

type FeatureKey =
  | "silvaCoin"
  | "learningHub"
  | "progressTracking"
  | "achievements"
  | "aiHelp"
  | "quizzes"
  | "mobile";

interface FeatureItem {
  key: FeatureKey;
  icon: typeof Trophy;
  gradient: string;
  /** Component-mockup variant rendered in the popup hero band. */
  mockup: MiniMockupVariant;
  ctaHref?: string;
}

const features: FeatureItem[] = [
  { key: "silvaCoin", icon: Trophy, gradient: "from-primary/20 to-accent/20", mockup: "streaks", ctaHref: "/gamification" },
  { key: "learningHub", icon: LayoutDashboard, gradient: "from-accent/20 to-secondary/20", mockup: "navigation", ctaHref: "/dashboard" },
  { key: "progressTracking", icon: TrendingUp, gradient: "from-secondary/20 to-primary/20", mockup: "progress", ctaHref: "/my-analytics" },
  { key: "achievements", icon: Award, gradient: "from-primary/20 to-accent/20", mockup: "certificates", ctaHref: "/gamification" },
  { key: "aiHelp", icon: Sparkles, gradient: "from-accent/20 to-secondary/20", mockup: "insights", ctaHref: "/courses" },
  { key: "quizzes", icon: ClipboardCheck, gradient: "from-secondary/20 to-primary/20", mockup: "quizzes", ctaHref: "/courses" },
  { key: "mobile", icon: Smartphone, gradient: "from-accent/20 to-secondary/20", mockup: "mobile" },
];

export const PlatformFeatures = memo(() => {
  const { t } = useTranslation();
  const { open, setOpen, activeKey, handleSelect } =
    useFeatureHoverPopup<FeatureKey>();

  const active = features.find((f) => f.key === activeKey) ?? null;
  const bullets = active
    ? (t(`featureDetails.${active.key}.bullets`, { returnObjects: true }) as unknown)
    : [];
  const bulletsArray = Array.isArray(bullets) ? (bullets as string[]) : [];

  return (
    <section className="py-12 lg:py-16 bg-gradient-subtle">
      <div className="container mx-auto px-4 sm:px-6 lg:px-12 xl:px-16 2xl:px-24">
        <div className="text-center mb-12 max-w-3xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="heading-page font-serif italic"
          >
            <span className="bg-gradient-primary bg-clip-text text-transparent leading-[1.2] inline-block pb-1">
              {t("platformFeatures.title")}
            </span>
          </motion.h2>
          <div className="divider-foil" />
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-sm md:text-base text-muted-foreground leading-relaxed"
          >
            {t("platformFeatures.subtitle")}
          </motion.p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-5 gap-6">
          {features.map((feature, index) => {
            const isActive = open && activeKey === feature.key;
            return (
              <motion.div
                key={feature.key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <button
                  type="button"
                  onClick={() => handleSelect(feature.key)}
                  aria-label={`${t(`platformFeatures.features.${feature.key}.title`)} — ${t("common.viewDetails", "View details")}`}
                  className="w-full h-full text-left"
                >
                  <Card
                    className={`relative h-full min-h-[340px] overflow-hidden bg-card/30 backdrop-blur-xl glass-border transition-colors duration-300 group ${
                      isActive
                        ? "border-primary/50 shadow-glass-hover"
                        : "hover:border-primary/40 hover:shadow-glass-hover"
                    }`}
                  >
                    {/* Mockup preview band */}
                    <div
                      className={`relative h-28 w-full overflow-hidden border-b glass-border bg-gradient-to-br ${feature.gradient}`}
                    >
                      <FeatureMiniMockup variant={feature.mockup} size="inline" />
                    </div>
                    <div
                      className={`absolute top-3 right-3 z-10 w-7 h-7 rounded-full bg-background/70 backdrop-blur-md border border-primary/30 flex items-center justify-center transition-opacity ${
                        isActive ? "opacity-100 bg-primary/20" : "opacity-80 group-hover:opacity-100 group-hover:bg-primary/20"
                      }`}
                    >
                      <Plus className="w-3.5 h-3.5 text-primary" />
                    </div>
                    <CardContent className="p-5">
                      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-3`}>
                        <feature.icon className="w-5 h-5 text-primary" />
                      </div>
                      <h3 className="text-base font-bold mb-1.5 text-foreground group-hover:text-primary transition-colors pr-8">
                        {t(`platformFeatures.features.${feature.key}.title`)}
                      </h3>
                      <p className="text-muted-foreground text-sm leading-relaxed">
                        {t(`platformFeatures.features.${feature.key}.description`)}
                      </p>
                    </CardContent>
                  </Card>
                </button>
              </motion.div>
            );
          })}
        </div>
      </div>

      {active && (
        <FeatureDetailDialog
          open={open}
          onOpenChange={setOpen}
          icon={active.icon}
          title={t(`platformFeatures.features.${active.key}.title`)}
          shortDescription={t(`platformFeatures.features.${active.key}.description`)}
          longDescription={t(`featureDetails.${active.key}.long`)}
          bullets={bulletsArray}
          bulletsTitle={t("featureDetails.whatYouCanDo")}
          customHero={<FeatureMiniMockup variant={active.mockup} />}
          screenshotAlt={t(`platformFeatures.features.${active.key}.title`)}
          ctaLabel={active.ctaHref ? t("featureDetails.tryItNow") : undefined}
          ctaHref={active.ctaHref}
          gradient={active.gradient}
        />
      )}
    </section>
  );
});

PlatformFeatures.displayName = "PlatformFeatures";
